import { useState } from 'react';
import { HandRaisedIcon } from '@heroicons/react/24/outline';

import type { IssueStatus, TechnicalIssue } from '../../../pages/housekeeping/types/housekeeping';
import { useChangerStatutPanne, usePrendrePanne } from '../../api/usePannes';
import Button from '../../components/Button';
import Text from '../../components/Text';
import { estTerminee, gestesStatut } from '../../helpers/maintenance';
import { t } from '../../i18n/langue';

/**
 * Le pied de la fiche panne : le gros « Je m'en occupe » tant que la panne est
 * libre, puis les gestes de statut (commencer, pause, résolu) pour celui qui l'a
 * prise — ou un responsable. Le retour est dit en ligne, jamais par un toast.
 */
interface PiedPanneProps {
    panne: TechnicalIssue;
    /** Le compte courant a-t-il la main sur les statuts (preneur ou responsable) ? */
    peutChangerStatut: boolean;
    onFait: (message: string) => void;
}

/** Des clés : traduites au moment du message, pas au chargement du module. */
const MESSAGES_STATUT: Partial<Record<IssueStatus, string>> = {
    in_progress: 'maintenance.pied.commencee',
    on_hold: 'maintenance.pied.enPause',
    resolved: 'maintenance.pied.resolue',
};

export default function PiedPanne({ panne, peutChangerStatut, onFait }: PiedPanneProps) {
    const prendre = usePrendrePanne();
    const changer = useChangerStatutPanne();
    const [alerte, setAlerte] = useState<string | null>(null);

    const libre = !panne.assigned_to_user_id && !estTerminee(panne.status);
    const gestes = peutChangerStatut ? gestesStatut(panne.status) : [];
    if (!libre && gestes.length === 0) return null;

    const occupe = prendre.isLoading || changer.isLoading;

    const prendreEnCharge = () => {
        setAlerte(null);
        prendre.mutate(
            { id: panne.id },
            {
                onSuccess: () => onFait(t('maintenance.pied.pris')),
                onError: (erreur) => {
                    if (erreur.response?.status === 409) {
                        const nom = erreur.response.data.assigned_to_name;
                        setAlerte(nom ? t('maintenance.pied.dejaPrisPar', { nom }) : t('maintenance.pied.dejaPris'));
                        return;
                    }
                    setAlerte(t('maintenance.pied.pasPris'));
                },
            },
        );
    };

    const passerA = (statut: IssueStatus) => {
        setAlerte(null);
        changer.mutate(
            { id: panne.id, status: statut },
            {
                onSuccess: () => onFait(t(MESSAGES_STATUT[statut] ?? 'maintenance.pied.statutMisAJour')),
                onError: (erreur) =>
                    setAlerte(
                        erreur.response?.status === 422
                            ? t('maintenance.pied.plusPossible')
                            : t('maintenance.pied.statutNonModifie'),
                    ),
            },
        );
    };

    return (
        <div className="flex flex-col gap-2 border-t border-border-color-light bg-white px-4 py-3">
            {alerte && (
                <div role="alert">
                    <Text variante="petit" ton="danger">
                        {alerte}
                    </Text>
                </div>
            )}
            {libre ? (
                <Button
                    libelle={t('maintenance.pied.jeMenOccupe')}
                    icone={HandRaisedIcon}
                    pleineLargeur
                    className="py-4"
                    disabled={occupe}
                    onPress={prendreEnCharge}
                />
            ) : (
                <div className="flex gap-2">
                    {gestes.map((geste, i) => (
                        <Button
                            key={geste.statut}
                            libelle={geste.libelle}
                            variante={i === 0 ? 'primaire' : 'secondaire'}
                            pleineLargeur
                            className="py-4"
                            disabled={occupe}
                            onPress={() => passerA(geste.statut)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
